import { useEffect, useMemo, useState } from "react";
import type { LocationFilter as LocationFilterValue } from "@resume-cloudflare/shared";
import { useCompanies } from "../lib/companies";
import {
  formatK,
  useJobs,
  useSimilarJobs,
  type JobRow,
  type JobsFilters,
} from "../lib/jobs";
import { useCreateApplication } from "../lib/applications";
import { useResumes } from "../lib/resumes";
import {
  useCachedSummary,
  useGenerateSummary,
  type FitSummary,
} from "../lib/summaries";
import {
  pdfHref as coverLetterPdfHref,
  useGenerateCoverLetter,
} from "../lib/cover_letters";
import { Button } from "../components/ui/Button";
import { Pill } from "../components/ui/Pill";
import { EmptyState } from "../components/ui/EmptyState";
import {
  TableShell,
  Table as UITable,
  THead,
  TRow,
  TCell,
  SortableTh,
  Th,
} from "../components/ui/Table";
import { Skeleton, SkeletonRow } from "../components/ui/Skeleton";
import { Sheet } from "../components/ui/Sheet";
import { Select } from "../components/ui/Select";
import {
  DEFAULT_LOCATION_FILTER,
  LocationFilter,
} from "../components/filters/LocationFilter";
import { useLocalStorageState } from "../lib/use_local_storage_state";
import { activateOnKey } from "../lib/keyboard";
import { ScorePill } from "../components/jobs/ScorePill";

const PAGE_SIZE = 100;
const FILTER_STORAGE_KEY = "jobs_filter_state";
const SORT_STORAGE_KEY = "jobs_sort";

type SortKey = "score_desc" | "created_desc";

const AXIS_LABELS: Array<{ key: keyof NonNullable<JobRow["score_axes"]>; label: string }> = [
  { key: "responsibilities", label: "Responsibilities" },
  { key: "seniority", label: "Seniority" },
  { key: "domain", label: "Domain" },
  { key: "tech_stack", label: "Tech stack" },
  { key: "location_fit", label: "Location fit" },
];

export default function Jobs() {
  const [locationFilter, setLocationFilter] = useLocalStorageState<LocationFilterValue>(
    FILTER_STORAGE_KEY,
    DEFAULT_LOCATION_FILTER,
  );
  const [sort, setSort] = useLocalStorageState<SortKey>(SORT_STORAGE_KEY, "score_desc");
  const [filters, setFilters] = useState<JobsFilters>({ limit: PAGE_SIZE });
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState<JobRow | null>(null);

  const effectiveFilters = useMemo<JobsFilters>(
    () => ({ ...filters, sort, location: locationFilter }),
    [filters, sort, locationFilter],
  );

  const companies = useCompanies();
  const q = useJobs(effectiveFilters);
  const rows = q.data?.jobs ?? [];

  function update(patch: Partial<JobsFilters>) {
    setFilters((prev) => ({ ...prev, ...patch, cursor: undefined }));
  }

  function applySearch() {
    update({ search: searchInput.trim() || undefined });
  }

  function clear() {
    setFilters({ limit: PAGE_SIZE });
    setSearchInput("");
    setLocationFilter(DEFAULT_LOCATION_FILTER);
  }

  return (
    <>
      <section className="grid grid-cols-1 gap-6 p-4 sm:p-6 lg:grid-cols-[16rem_1fr] lg:p-8">
        <aside className="space-y-5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 self-start lg:sticky lg:top-4">
          <h2 className="text-xs uppercase tracking-wider text-zinc-500">
            Filters
          </h2>

          <Field label="Tier">
            <select
              className={fieldCls}
              value={filters.tier ?? ""}
              onChange={(e) =>
                update({
                  tier: e.target.value
                    ? (Number(e.target.value) as 1 | 2 | 3)
                    : undefined,
                })
              }
            >
              <option value="">All tiers</option>
              <option value="1">Tier 1</option>
              <option value="2">Tier 2</option>
              <option value="3">Tier 3</option>
            </select>
          </Field>

          <Field label="Company">
            <select
              className={fieldCls}
              value={filters.company ?? ""}
              onChange={(e) =>
                update({ company: e.target.value || undefined })
              }
            >
              <option value="">All companies</option>
              {(companies.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Search">
            <input
              type="search"
              className={fieldCls}
              value={searchInput}
              placeholder="Title, location…"
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch();
              }}
              onBlur={applySearch}
            />
          </Field>

          <Field label="Max age">
            <select
              className={fieldCls}
              value={filters.maxAgeDays ?? ""}
              onChange={(e) =>
                update({
                  maxAgeDays: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            >
              <option value="">Any age</option>
              <option value="1">Today</option>
              <option value="3">3 days</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
            </select>
          </Field>

          <div>
            <span className="block text-[11px] uppercase tracking-wider text-zinc-500">
              Location
            </span>
            <div className="mt-1">
              <LocationFilter value={locationFilter} onChange={setLocationFilter} />
            </div>
          </div>

          <button
            className="w-full rounded border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
            onClick={clear}
          >
            Clear filters
          </button>
        </aside>

        <div className="min-w-0">
          <header className="flex items-baseline justify-between">
            <h1 className="text-2xl font-semibold tracking-tight">
              Matching Jobs
            </h1>
            <span className="text-xs text-neutral-500" aria-live="polite">
              {q.data
                ? `${q.data.returned} shown · ${q.data.total_active} total active`
                : q.isLoading ? (
                    <Skeleton className="inline-block h-3 w-40" />
                  ) : null}
            </span>
          </header>

          <div className="mt-4">
            {q.isLoading ? (
              <TableShell>
                <UITable>
                  <THead>
                    <tr>
                      <SortHeader sort={sort} setSort={setSort} />
                    </tr>
                  </THead>
                  <tbody>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <SkeletonRow key={i} cols={5} />
                    ))}
                  </tbody>
                </UITable>
              </TableShell>
            ) : q.error ? (
              <TableShell>
                <EmptyState
                  tone="danger"
                  title="Couldn't load jobs"
                  description={String(q.error)}
                  action={
                    <Button variant="secondary" onClick={() => q.refetch()}>
                      Retry
                    </Button>
                  }
                />
              </TableShell>
            ) : rows.length === 0 ? (
              <TableShell>
                <EmptyState
                  title="No matching jobs. Try removing filters."
                  action={
                    <Button variant="secondary" onClick={clear}>
                      Clear filters
                    </Button>
                  }
                />
              </TableShell>
            ) : (
              <TableShell>
                <UITable>
                  <THead>
                    <tr>
                      <SortHeader sort={sort} setSort={setSort} />
                    </tr>
                  </THead>
                  <tbody>
                    {rows.map((row) => {
                      const isActive =
                        selected !== null &&
                        selected.company_id === row.company_id &&
                        selected.job_id === row.job_id;
                      return (
                        <TRow
                          key={`${row.company_id}:${row.job_id}`}
                          className={`cursor-pointer hover:bg-neutral-900/60 ${
                            isActive ? "bg-neutral-900/80" : ""
                          }`}
                          tabIndex={0}
                          role="button"
                          onClick={() => setSelected(row)}
                          onKeyDown={(e) => activateOnKey(e, () => setSelected(row))}
                          aria-selected={isActive}
                        >
                          <TCell>
                            <FitCell row={row} />
                          </TCell>
                          <TCell>{row.company_name}</TCell>
                          <TCell className="font-medium text-neutral-100">
                            {row.title}
                          </TCell>
                          <TCell>
                            <LocationCell row={row} />
                          </TCell>
                          <TCell>
                            <CompCell row={row} />
                          </TCell>
                          <TCell className="text-neutral-500">
                            {ageLabel(row.first_seen_at)}
                          </TCell>
                        </TRow>
                      );
                    })}
                  </tbody>
                </UITable>
              </TableShell>
            )}
          </div>
        </div>
      </section>

      <Sheet
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
      >
        <Sheet.Content side="right">
          {selected ? (
            <JobSidePanel
              key={selected.url}
              job={selected}
              onSelectSimilar={setSelected}
            />
          ) : null}
        </Sheet.Content>
      </Sheet>
    </>
  );
}

function SortHeader({
  sort,
  setSort,
}: {
  sort: SortKey;
  setSort: (s: SortKey) => void;
}) {
  return (
    <>
      <SortableTh
        label="Fit"
        active={sort === "score_desc"}
        dir="desc"
        onSort={() => setSort("score_desc")}
      />
      <Th>Company</Th>
      <Th>Title</Th>
      <Th>Location</Th>
      <Th>Est. TTC</Th>
      <SortableTh
        label="First seen"
        active={sort === "created_desc"}
        dir="desc"
        onSort={() => setSort("created_desc")}
      />
    </>
  );
}

function FitCell({ row }: { row: JobRow }) {
  if (row.score === null || row.score_axes === null) {
    return <ScorePill score={null} />;
  }
  return (
    <ScorePill
      score={row.score}
      tooltip={<AxesBreakdown axes={row.score_axes} rationale={row.score_rationale} />}
    />
  );
}

function AxesBreakdown({
  axes,
  rationale,
}: {
  axes: NonNullable<JobRow["score_axes"]>;
  rationale: string | null;
}) {
  return (
    <div className="space-y-2">
      <ul className="space-y-0.5 tabular-nums">
        {AXIS_LABELS.map(({ key, label }) => (
          <li key={key} className="flex justify-between gap-4">
            <span className="text-neutral-400">{label}</span>
            <span className="text-neutral-100">{axes[key]}</span>
          </li>
        ))}
      </ul>
      {rationale ? (
        <p className="border-t border-neutral-700 pt-1 text-neutral-300">{rationale}</p>
      ) : null}
    </div>
  );
}

function LocationCell({ row }: { row: JobRow }) {
  if (row.location_facts === null) {
    return (
      <div className="space-y-0.5">
        <Pill tone="neutral">Location unclear</Pill>
        {row.location ? (
          <p className="truncate text-[11px] text-neutral-500">{row.location}</p>
        ) : null}
      </div>
    );
  }
  const tone =
    row.location_eligible_for_user === true
      ? "text-success-fg"
      : row.location_eligible_for_user === false
        ? "text-warning-fg"
        : "text-neutral-300";
  return (
    <span className={tone}>{row.location_summary ?? row.location ?? "—"}</span>
  );
}

// Est. yearly TTC over ~3 years (posted pay midpoint + modeled equity ×
// liquidity haircut). The basis string rides in the title attribute.
function CompCell({ row }: { row: JobRow }) {
  const est = row.comp_estimate;
  if (!est) return <span className="text-neutral-600">—</span>;
  return (
    <span
      className={`tabular-nums ${est.typical >= 300_000 ? "text-success-fg" : "text-neutral-300"}`}
      title={`${formatK(est.low)}–${formatK(est.high)} · ${est.basis} · ${est.confidence} confidence`}
    >
      {formatK(est.typical)}
    </span>
  );
}

function ageLabel(firstSeenAt: number): string {
  const days = Math.floor((Date.now() - firstSeenAt) / 86400_000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day";
  if (days < 30) return `${days} days`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month" : `${months} months`;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-zinc-500">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const fieldCls =
  "w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-sm text-zinc-100 outline-none focus:border-zinc-600";

function JobSidePanel({
  job,
  onSelectSimilar,
}: {
  job: JobRow;
  onSelectSimilar: (row: JobRow) => void;
}) {
  const createApp = useCreateApplication();
  const lastResult = createApp.data;

  const resumes = useResumes();
  const resumeRows = resumes.data?.resumes ?? [];
  const [resumeId, setResumeId] = useState<string>("");
  useEffect(() => {
    if (!resumeId && resumeRows.length > 0) {
      setResumeId(resumeRows[0]!.id);
    }
  }, [resumeRows, resumeId]);

  const summary = useGenerateSummary();
  const coverLetter = useGenerateCoverLetter();
  const cachedQuery = useCachedSummary(job.url);
  const cached = cachedQuery.data ?? null;
  const cacheLoaded = cachedQuery.isFetched;

  const visibleSummary = summary.data?.summary ?? cached;

  function trackAs(status: "interested" | "applied") {
    createApp.mutate({
      url: job.url,
      role: job.title,
      company_id: job.company_id,
      status,
    });
  }

  function generate(force: boolean) {
    if (!resumeId) return;
    summary.mutate({ url: job.url, resume_id: resumeId, force });
  }

  function generateCoverLetter(force: boolean) {
    if (!resumeId) return;
    coverLetter.mutate({ url: job.url, resume_id: resumeId, force });
  }

  const coverLetterRow = coverLetter.data?.cover_letter;

  return (
    <>
      <Sheet.Header
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <span>{job.company_name}</span>
            <Pill tone="neutral">Tier {job.company_tier}</Pill>
          </span>
        }
        title={job.title}
      />
      <Sheet.Body className="space-y-6">
        <ScoreBreakdown job={job} />

        <dl className="grid grid-cols-1 gap-3 text-sm">
          <Row label="Location" value={job.location_summary ?? job.location ?? "—"} />
          <Row
            label="Posted pay"
            value={
              job.pay
                ? `${formatK(job.pay.min)}–${formatK(job.pay.max)} ${job.pay.is_ote ? "OTE" : "base"}${
                    job.pay.equity_offered ? " + equity" : ""
                  }`
                : "Not posted"
            }
          />
          <Row
            label="Est. TTC (3-yr)"
            value={
              job.comp_estimate
                ? `${formatK(job.comp_estimate.typical)} (${formatK(job.comp_estimate.low)}–${formatK(
                    job.comp_estimate.high,
                  )}, ${job.comp_estimate.confidence} confidence) · ${job.comp_estimate.basis}`
                : "—"
            }
          />
          <Row label="Department" value={job.department ?? "—"} />
          <Row label="First seen" value={ageLabel(job.first_seen_at)} />
          <Row label="Last seen" value={ageLabel(job.last_seen_at)} />
          <Row label="ATS updated" value={job.updated_at ?? "—"} />
        </dl>

        <div className="space-y-2">
          <Button
            variant="primary"
            className="w-full"
            onClick={() => window.open(job.url, "_blank", "noopener,noreferrer")}
          >
            Open posting
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              size="sm"
              loading={createApp.isPending}
              onClick={() => trackAs("interested")}
            >
              Track as interested
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={createApp.isPending}
              onClick={() => trackAs("applied")}
            >
              Mark applied
            </Button>
          </div>
          {lastResult && !createApp.error ? (
            <p className="text-[11px] text-neutral-500">
              {lastResult.already_existed
                ? `Already tracked as ${lastResult.application.status}.`
                : `Tracked as ${lastResult.application.status}.`}
            </p>
          ) : null}
        </div>

        <SimilarJobsSection
          companyId={job.company_id}
          jobId={job.job_id}
          onSelect={onSelectSimilar}
        />

        <section className="space-y-3 border-t border-neutral-800 pt-4">
          <h3 className="text-xs uppercase tracking-wider text-neutral-500">
            Fit summary
          </h3>
          {resumeRows.length === 0 ? (
            <p className="text-xs text-neutral-400">
              Upload a resume on the{" "}
              <a href="/resumes" className="underline hover:text-neutral-100">
                Resumes
              </a>{" "}
              page first.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Select
                  className="h-8 text-xs"
                  value={resumeId}
                  onChange={(e) => setResumeId(e.target.value)}
                  aria-label="Resume for fit summary"
                >
                  {resumeRows.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  variant="primary"
                  loading={summary.isPending}
                  disabled={!resumeId}
                  onClick={() => generate(visibleSummary !== null)}
                >
                  {visibleSummary ? "Regenerate" : "Generate"}
                </Button>
              </div>
              {visibleSummary ? (
                <SummaryView summary={visibleSummary} />
              ) : cacheLoaded && !summary.isPending ? (
                <p className="text-[11px] text-neutral-500">
                  No summary yet for this posting.
                </p>
              ) : null}
            </>
          )}
        </section>

        <section className="space-y-3 border-t border-neutral-800 pt-4">
          <h3 className="text-xs uppercase tracking-wider text-neutral-500">
            Cover letter
          </h3>
          {resumeRows.length === 0 ? (
            <p className="text-xs text-neutral-400">
              Upload a resume on the{" "}
              <a href="/resumes" className="underline hover:text-neutral-100">
                Resumes
              </a>{" "}
              page first.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  loading={coverLetter.isPending}
                  disabled={!resumeId}
                  onClick={() => generateCoverLetter(coverLetterRow !== undefined)}
                >
                  {coverLetterRow ? "Regenerate cover letter" : "Generate cover letter"}
                </Button>
                {coverLetterRow ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      window.open(
                        coverLetterPdfHref(coverLetterRow.id),
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    Download PDF
                  </Button>
                ) : null}
              </div>
              <p className="text-[11px] text-neutral-500">
                Uses {resumeRows.find((r) => r.id === resumeId)?.label ?? "the selected resume"}. Sonnet — takes 20-40s.
              </p>
              {coverLetterRow ? (
                <div className="rounded-md border border-neutral-800 bg-neutral-900/40 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-neutral-500">
                    {coverLetter.data?.cached ? "Cached" : "Generated"} ·{" "}
                    {coverLetterRow.model}
                  </p>
                  <pre className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-neutral-200">
                    {coverLetterRow.text}
                  </pre>
                </div>
              ) : null}
            </>
          )}
        </section>
      </Sheet.Body>
    </>
  );
}

function SimilarJobsSection({
  companyId,
  jobId,
  onSelect,
}: {
  companyId: string;
  jobId: string;
  onSelect: (row: JobRow) => void;
}) {
  const q = useSimilarJobs(companyId, jobId, 10);

  return (
    <section className="space-y-3 border-t border-neutral-800 pt-4">
      <h3 className="text-xs uppercase tracking-wider text-neutral-500">
        Similar jobs
      </h3>
      {q.isLoading ? (
        <ul className="space-y-2">
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <Skeleton className="h-10 w-full" />
            </li>
          ))}
        </ul>
      ) : q.error === "not_embedded" ? (
        <p className="text-[11px] text-neutral-500">
          Not yet embedded — once the next cron tick processes this posting it
          will have neighbours.
        </p>
      ) : q.error ? (
        <p className="text-[11px] text-danger-fg">Couldn't load similar jobs.</p>
      ) : !q.data || q.data.returned === 0 ? (
        <p className="text-[11px] text-neutral-500">No similar postings found.</p>
      ) : (
        <ul className="divide-y divide-neutral-800">
          {q.data.jobs.map((row) => (
            <SimilarJobRow key={row.url} job={row} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </section>
  );
}

function SimilarJobRow({
  job,
  onSelect,
}: {
  job: JobRow;
  onSelect: (row: JobRow) => void;
}) {
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={() => onSelect(job)}
      onKeyDown={(e) => activateOnKey(e, () => onSelect(job))}
      className="flex cursor-pointer items-center gap-3 py-2 first:pt-0 last:pb-0 hover:bg-neutral-900/40"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500">
          {job.company_name}
        </p>
        <p className="truncate text-xs font-medium text-neutral-100">
          {job.title}
        </p>
        {job.location_summary ? (
          <p className="truncate text-[11px] text-neutral-500">
            {job.location_summary}
          </p>
        ) : null}
      </div>
      <ScorePill score={job.score} />
    </li>
  );
}

function ScoreBreakdown({ job }: { job: JobRow }) {
  if (job.score === null || job.score_axes === null) {
    return (
      <section className="rounded-md border border-neutral-800 bg-neutral-900/40 p-3 text-xs text-neutral-400">
        Not yet scored against your primary resume. Set one on{" "}
        <a href="/settings" className="underline hover:text-neutral-100">
          Settings
        </a>{" "}
        and the next cron tick will score it.
      </section>
    );
  }
  return (
    <section className="space-y-3 rounded-md border border-neutral-800 bg-neutral-900/40 p-3">
      <div className="flex items-center gap-2">
        <ScorePill score={job.score} />
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">
          Fit score
        </span>
      </div>
      <ul className="space-y-1.5 text-xs tabular-nums">
        {AXIS_LABELS.map(({ key, label }) => {
          const v = job.score_axes![key];
          return (
            <li key={key} className="grid grid-cols-[7rem_1fr_2ch] items-center gap-2">
              <span className="text-neutral-400">{label}</span>
              <span
                className="h-1.5 rounded-full bg-neutral-800"
                aria-hidden
              >
                <span
                  className="block h-full rounded-full bg-accent-500"
                  style={{ width: `${(v / 10) * 100}%` }}
                />
              </span>
              <span className="text-right text-neutral-100">{v}</span>
            </li>
          );
        })}
      </ul>
      {job.score_rationale ? (
        <p className="border-t border-neutral-800 pt-2 text-xs italic leading-relaxed text-neutral-300">
          {job.score_rationale}
        </p>
      ) : null}
    </section>
  );
}

function SummaryView({ summary }: { summary: FitSummary }) {
  const tone: "success" | "warning" | "neutral" =
    summary.fit >= 8 ? "success" : summary.fit >= 6 ? "warning" : "neutral";
  return (
    <div className="space-y-3 text-xs">
      <div className="flex items-center gap-2">
        <Pill tone={tone}>Fit {summary.fit}/10</Pill>
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">
          {summary.model}
        </span>
      </div>
      <p className="leading-relaxed text-neutral-200">{summary.summary}</p>
      {summary.strengths.length > 0 ? (
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-wider text-success-fg">
            Strengths
          </p>
          <ul className="list-disc space-y-0.5 pl-4 text-neutral-300">
            {summary.strengths.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {summary.gaps.length > 0 ? (
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-wider text-danger-fg">
            Gaps
          </p>
          <ul className="list-disc space-y-0.5 pl-4 text-neutral-300">
            {summary.gaps.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="border-t border-neutral-800 pt-2 italic text-neutral-300">
        {summary.verdict}
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3">
      <dt className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </dt>
      <dd className="text-neutral-200">{value}</dd>
    </div>
  );
}
